import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Locale } from "@/i18n";
import { localePath } from "@/lib/nav";

const STORAGE_KEY = "lc_cookie_consent_v1";

type Consent = "accepted" | "rejected";

export default function CookieConsentBanner() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language as Locale) || "it";
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return !localStorage.getItem(STORAGE_KEY);
    } catch {
      return true;
    }
  });

  const setChoice = (choice: Consent) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() }),
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("cookie.banner.aria")}
      className="fixed inset-x-4 bottom-4 md:inset-x-auto md:bottom-6 md:left-6 md:right-6 z-[100]"
    >
      <div className="mx-auto max-w-[1380px] rounded-[24px] bg-background/95 backdrop-blur-xl border border-foreground/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.25)] px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1 text-[13px] md:text-[14px] leading-[1.65] text-foreground/75">
          <p>
            {t("cookie.banner.message")}{" "}
            <Link
              to={localePath(locale, "cookies")}
              className="underline underline-offset-4 text-foreground hover:text-foreground/70 transition"
            >
              {t("cookie.banner.read_more")}
            </Link>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setChoice("rejected")}
            className="rounded-full px-5 py-2.5 text-[13px] font-medium border border-foreground/25 text-foreground/80 hover:border-foreground hover:text-foreground transition"
          >
            {t("cookie.banner.reject")}
          </button>
          <button
            type="button"
            onClick={() => setChoice("accepted")}
            className="rounded-full px-5 py-2.5 text-[13px] font-medium bg-foreground text-background border border-foreground hover:bg-foreground/85 transition"
          >
            {t("cookie.banner.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}